import React, { useCallback, useMemo } from 'react';
import { css } from '@emotion/css';

import { useTranslation } from '@lyearn/i18n';
import { EmptyState } from '@lyearn/molecules';
import { cn } from '@lyearn/style';
import { Stack } from '@lyearn/ui';
import { listContainerStyles } from '@/components/ContentList/components/Items/styles';
import { parseVisibilitySettingsRestrictionConfig } from '@/components/Form/components/VisibilitySettings/utils';
import Loader from '@/components/Loader';
import SearchBarWithCount from '@/components/SearchBar/SearchBarWithCount';
import VirtualizedList from '@/components/VirtualizedList';
import useDebounceState from '@/hooks/useDebounceState';
import LearnerListItem from '@/modules/corporate/modules/assignment/views/AssignmentCreator/components/SelectLearnersDialog/components/ListItem';

import { RestrictedVisibilitySettingsTabType } from '../RestrictedVisibilitySettingsGroupsTab/types';
import useRestrictedVisibilitySettingsUsersTab from './hooks/useRestrictedVisibilitySettingsUsersTab';

const CLASSES = {
  container: css`
    padding-inline-end: 0.8rem !important;
    padding-inline-start: 0.8rem !important;
  `,
};

const RestrictedVisibilitySettingsUsersTab: React.FC<RestrictedVisibilitySettingsTabType> = (
  props,
) => {
  const { t } = useTranslation();
  const { onChange, disabled, value } = props;
  const { userIds } = useMemo(() => parseVisibilitySettingsRestrictionConfig(value), [value]);

  const [searchText, setSearchText, debounceSearchText] = useDebounceState('');

  const { users, fetchingUsers, toggleSelectedUserIds, selectedUserIdsSet, fetchMoreUsers } =
    useRestrictedVisibilitySettingsUsersTab({ value, onChange, disabled, debounceSearchText });

  const renderer = useCallback(() => {
    if (fetchingUsers) {
      return <Loader />;
    }

    if (!users.length) {
      if (searchText) {
        return (
          <EmptyState
            className="flex-1"
            description={t('Group:Modal.SelectUsersEmptyDescription', { text: searchText })}
            title={t('Group:Modal.SelectUsersEmptyTitle')}
          />
        );
      }

      return (
        <EmptyState
          className="flex-1"
          description={t('Group:Modal.NoUsersDescription')}
          title={t('Group:Modal.NoUsersAvailable')}
        />
      );
    }

    return (
      <VirtualizedList
        className={cn(listContainerStyles, 'flex-1')}
        data={users}
        endReached={fetchMoreUsers}
        itemContent={(index) => {
          const user = users[index];

          return (
            <LearnerListItem
              key={user.id}
              classes={CLASSES}
              profilePictureSize={40}
              selected={selectedUserIdsSet.has(user.id)}
              user={user}
              onSelect={toggleSelectedUserIds}
            />
          );
        }}
      />
    );
  }, [
    fetchMoreUsers,
    fetchingUsers,
    searchText,
    selectedUserIdsSet,
    t,
    toggleSelectedUserIds,
    users,
  ]);

  return (
    <>
      <Stack className="pb-12 pe-16 ps-24">
        <SearchBarWithCount
          placeholder={t('Common:Search')}
          selectedCount={userIds.length}
          shape="square"
          value={searchText}
          onChange={setSearchText}
        />
      </Stack>
      {renderer()}
    </>
  );
};

export default RestrictedVisibilitySettingsUsersTab;
